import React from 'react';
import {Clock, Scissors} from 'lucide-react';
import ActionMenu from '../components/Common/ActionMenu';
import {statusLabel, statusStyle} from './calendarColors';
import {formatTime} from './calendarRange';

// Appointment card shared by the Day timeline and the Week agenda: soft status
// tint, a left accent in the professional's colour, and content that folds
// away as the card gets smaller (`height` in px drives what is shown).
// `getActions(event)` feeds the "⋯" menu shown on hover (ActionMenu items).

const AppointmentCard = ({event, resource, height, showResource = false, onClick, getActions, style}) => {
    const s = statusStyle(event.status);
    const cancelled = event.status === 'cancelled';
    const services = event.selectedServices || [];
    const roomy = height >= 64;

    return (
        <div role="button" tabIndex={0}
             onClick={(e) => { e.stopPropagation(); onClick(event); }}
             onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onClick(event); } }}
             title={`${event.clientName || event.title} · ${formatTime(event.start)} – ${formatTime(event.end)}${resource ? ` · ${resource.name}` : ''} · ${statusLabel(event.status)}`}
             className={`group absolute z-10 flex flex-col overflow-hidden rounded-lg border pl-2.5 pr-1.5 py-1 cursor-pointer
                         shadow-sm transition-all duration-150 hover:z-20 hover:shadow-md hover:-translate-y-px
                         focus:outline-none focus-visible:ring-2 focus-visible:ring-primary ${s.card}`}
             style={{...style, height}}>
            {/* Professional accent */}
            <span className="absolute left-0 top-0 bottom-0 w-1" style={{backgroundColor: resource?.color || '#4DB6AC'}}/>

            <div className="flex items-start gap-1 min-w-0">
                <span className={`flex-1 min-w-0 truncate text-xs font-semibold leading-tight ${cancelled ? 'line-through' : ''}`}>
                    {event.clientName || event.title}
                </span>
                {roomy && (
                    <span className={`flex-shrink-0 inline-flex items-center gap-1 rounded-full px-1.5 py-px text-[10px] font-semibold ${s.pill}`}>
                        <span className={`w-1.5 h-1.5 rounded-full ${s.dot}`}/>
                        {statusLabel(event.status)}
                    </span>
                )}
                {!roomy && <span className={`flex-shrink-0 mt-1 w-1.5 h-1.5 rounded-full ${s.dot}`}/>}
                {getActions && (
                    // Clicks inside the menu (portalled, but bubbling through React) must not open the RDV
                    <span onClick={(e) => e.stopPropagation()} onKeyDown={(e) => e.stopPropagation()}
                          className="flex-shrink-0 -mt-0.5 -mr-0.5 opacity-0 group-hover:opacity-100 focus-within:opacity-100 has-[[aria-expanded=true]]:opacity-100 transition-opacity">
                        <ActionMenu items={getActions(event)} label="Actions du rendez-vous"
                                    trigger={{size: 'sm', variant: 'ghost', className: '!h-6 !w-6 !rounded-md bg-white/70 dark:bg-gray-900/40'}}/>
                    </span>
                )}
            </div>

            {height >= 36 && (
                <div className="flex items-center gap-1 mt-0.5 text-[11px] leading-tight opacity-75 tabular-nums min-w-0">
                    <Clock className="w-3 h-3 flex-shrink-0"/>
                    <span className="truncate">{formatTime(event.start)} – {formatTime(event.end)}</span>
                </div>
            )}

            {showResource && resource && height >= 52 && (
                <div className="flex items-center gap-1 mt-0.5 text-[11px] leading-tight opacity-75 min-w-0">
                    <span className="w-3 h-3 rounded flex-shrink-0 flex items-center justify-center text-[8px] font-bold text-white"
                          style={{backgroundColor: resource.color}}>
                        {(resource.name || '?').charAt(0).toUpperCase()}
                    </span>
                    <span className="truncate">{resource.name}</span>
                </div>
            )}

            {services.length > 0 && height >= (showResource ? 76 : 56) && (
                <div className="flex items-center gap-1 mt-0.5 text-[11px] leading-tight opacity-75 min-w-0">
                    <Scissors className="w-3 h-3 flex-shrink-0"/>
                    <span className="truncate">{services.join(', ')}</span>
                </div>
            )}

            {event._syncStatus === 'pending' && (
                <span className="absolute bottom-1 right-1.5 w-1.5 h-1.5 rounded-full bg-amber-400 animate-pulse" title="Synchronisation en attente"/>
            )}
        </div>
    );
};

export default AppointmentCard;
